import { createContext, useContext, useState, useEffect } from 'react';

const LocalizationContext = createContext();

const translations = {
  en: {
    // Dashboard
    dashboard: 'Dashboard',
    total_products: 'Total Products',
    total_orders: 'Total Orders',
    pending_orders: 'Pending Orders',
    revenue: 'Revenue',
    // Navigation
    main_menu: 'MAIN MENU',
    catalog: 'CATALOG',
    marketing: 'MARKETING',
    // Orders
    order_management: 'Order Management',
    order_management_desc: 'Manage and track all customer orders',
    refresh: 'Refresh',
    pending: 'Pending',
    packed: 'Packed',
    shipped: 'Shipped',
    completed: 'Completed',
    // Products
    product_management: 'Product Management',
    product_management_desc: 'Manage your luxury perfume collection',
    export_excel: 'Export to Excel',
    add_new_product: 'Add New Product',
    products: 'Products',
    // Brands
    brands: 'Brands',
    add_new_brand: 'Add New Brand',
    // Categories
    categories: 'Categories',
    add_new_category: 'Add New Category',
    // Product Types
    product_types: 'Product Types',
    add_product_type: 'Add Product Type',
    // Advertisements
    advertisements: 'Advertisements',
    add_advertisement: 'Add Advertisement',
    // Discounts
    discounts_promotions: 'Discounts & Promotions',
    manage_discounts_promotions: 'Manage discounts and promotions',
    product_discounts: 'Product Discounts',
    shipping_discounts: 'Shipping Discounts',
    promotion_banners: 'Promotion Banners',
    // Fragrance Notes
    fragrance_notes: 'Fragrance Notes',
    add_new_note: 'Add New Note',
    // Users
    admin_users: 'Admin Users',
    add_new_user: 'Add New User',
    // Common
    loading_label: 'Loading...',
    search: 'Search...',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    logout: 'Logout',
  },
  ar: {
    // Dashboard
    dashboard: 'لوحة التحكم',
    total_products: 'المنتجات',
    total_orders: 'إجمالي الطلبات',
    pending_orders: 'طلبات معلقة',
    revenue: 'الإيرادات',
    // Navigation
    main_menu: 'القائمة الرئيسية',
    catalog: 'الكتالوج',
    marketing: 'التسويق',
    // Orders
    order_management: 'إدارة الطلبات',
    order_management_desc: 'إدارة وتتبع جميع طلبات العملاء',
    refresh: 'تحديث',
    pending: 'معلق',
    packed: 'تم التعبئة',
    shipped: 'تم الشحن',
    completed: 'مكتمل',
    // Products
    product_management: 'إدارة المنتجات',
    product_management_desc: 'إدارة مجموعة العطور الفاخرة الخاصة بك',
    export_excel: 'تصدير إلى Excel',
    add_new_product: 'إضافة منتج جديد',
    products: 'المنتجات',
    // Brands
    brands: 'العلامات التجارية',
    add_new_brand: 'إضافة علامة تجارية جديدة',
    // Categories
    categories: 'التصنيفات',
    add_new_category: 'إضافة تصنيف جديد',
    // Product Types
    product_types: 'أنواع المنتجات',
    add_product_type: 'إضافة نوع منتج',
    // Advertisements
    advertisements: 'الإعلانات',
    add_advertisement: 'إضافة إعلان',
    // Discounts
    discounts_promotions: 'الخصومات والعروض',
    manage_discounts_promotions: 'إدارة الخصومات والعروض',
    product_discounts: 'خصومات المنتجات',
    shipping_discounts: 'خصومات الشحن',
    promotion_banners: 'لافتات الترويج',
    // Fragrance Notes
    fragrance_notes: 'ملاحظات العطر',
    add_new_note: 'إضافة ملاحظة جديدة',
    // Users
    admin_users: 'مستخدمي الإدارة',
    add_new_user: 'إضافة مستخدم جديد',
    // Common
    loading_label: 'جاري التحميل...',
    search: 'بحث...',
    actions: 'الإجراءات',
    edit: 'تعديل',
    delete: 'حذف',
    view: 'عرض',
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    logout: 'تسجيل الخروج',
  },
};

export const LocalizationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language') || 'ar'
  );

  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', currentLanguage);
  }, [currentLanguage]);

  const setLanguage = (language) => {
    setCurrentLanguage(language);
  };

  const get = (key) => {
    return translations[currentLanguage]?.[key] || key;
  };

  const getDirection = () => {
    return currentLanguage === 'ar' ? 'rtl' : 'ltr';
  };

  const isRtl = currentLanguage === 'ar';

  const value = {
    currentLanguage,
    setLanguage,
    get,
    getDirection,
    isRtl,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};
